import get from "lodash/get";
import find from "lodash/find";
import map from "lodash/map";
import forEach from "lodash/forEach";
import { getStateData } from "analytics/index";
import { getChannelFromUrl, urlParam } from "utils/helpers";
import { GTM_ECOMMERCE_EVENT_NAMES } from "constants/AnalyticsTypeConstants";
import applicationConfig from "@/ui-config/launch-config";
import { CONTRACT_TYPES, PLAN_TYPE, BRAND_NAME } from "utils/constants";
import {
  getIpAddress,
  getPromoCode,
  isWebview,
  isRetail,
  getItem
} from "utils/localStorage";
import Sha256 from "crypto-js/sha256";
import { isEmpty } from "lodash";

const SIM_TYPE_MAPPING = {
  PHYSICAL_SIM: "Physical SIM",
  PHYSICALSIM: "Physical SIM",
  ESIM: "ESIM",
  E_SIM: "ESIM",
  eSIM: "ESIM"
};

const NUMBER_TYPE_MAPPING = {
  PORTIN_NUMBER: "Transfer Number",
  NEW_NUMBER: "New Number",
  PORT_IN: "Transfer Number",
  NEW: "New Number"
};

const ITEM_CATEGORY = {
  PLAN: "Plan",
  ADDON: "Add-on"
};

const ORDER_CHANNELS = {
  WEB: "web",
  APP: "App",
  RETAIL: "retail",
  DIGITAL: "digital"
};

const CNIC_ERROR = "error_invalid_identification_number";
const ORDER_PLATFORM = isWebview() ? ORDER_CHANNELS.APP : ORDER_CHANNELS.WEB;
const getOrderChannel = reduxStore => {
  return getStateData(reduxStore, "api.storeIdValidationInfo.data.valid", null)
    ? get(ORDER_CHANNELS, "RETAIL", null)
    : get(ORDER_CHANNELS, "DIGITAL", null);
};

export const FIELDS_TO_TRACK = {
  PERSONAL_DETAILS_FORM_START: "first_name", // change value as per field name of the start of personal details form
  PREPAID_DELIVERY_DETAIL_FORM_START: "delivery_address_line_1",
  POSTPAID_DELIVERY_DETAIL_FORM_START: "billing_address_line_1",
  IDENTIFICATION_NUMBER: "identification_number",
  CONTACT_NUMBER: "contact_number",
  NATIONALITY: "nationality",
  PORTIN_NUMBER_CURRENT_NUMBER: "port_number",
  PORTIN_NUMBER_CURRENT_TECLCO: "telco",
  CONFIRM_IDENTIFICATION_NUMBER: "reConfirmidentificationNumber",
  PROVINCE_SELECTED:"delivery_province",
  EMAIL:"email"

};

const JOURNEY_TYPE =
  urlParam("channel") && urlParam("channel").toLowerCase() === "easypaisa"
    ? "EasyPaisa"
    : "Regular";

const constructItems = (allUserSelectedItems = [], brand = BRAND_NAME) =>
  map(
    allUserSelectedItems,
    (selectedAddon, index) => ({
      item_name: get(selectedAddon, "title", null),
      item_id: get(selectedAddon, "sku", null),
      price: get(selectedAddon, "price", null),
      item_brand: brand || null,
      item_category:
        index === 0 ? get(ITEM_CATEGORY, "PLAN") : get(ITEM_CATEGORY, "ADDON"),
      index,
      quantity: 1
    }),
    []
  );

export const constructViewItemPayload = (
  data,
  reduxStore,
  userSelectedPlan = {},
  allUserSelectedItems = []
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "VIEW_ITEM", ""),
    ecommerce: {
      currency: getStateData(
        reduxStore,
        "api.appGlobalSettings.data.currency",
        null
      ),
      value: allUserSelectedItems.reduce(
        (total, selectedAddon) => total + get(selectedAddon, "price", 0),
        0
      ),
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      sim_count: 1,
      items: constructItems(allUserSelectedItems),
      order_platform: ORDER_PLATFORM,
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const constructViewItemListPayload = (data, reduxStore) => {
  const currentPath = window.location.pathname;
  let items = [];
  const currentChannel = getChannelFromUrl();
  const workflows = get(applicationConfig, "workflows");
  const productVariants = get(data, "productVariants");
  const channelWorkflows = get(workflows[currentChannel], "workflow");
  const currentWorkflow = find(channelWorkflows, { path: currentPath });
  const workflowComponents = get(currentWorkflow, "components");
  const numProductsShown = get(
    find(workflowComponents, { name: "PLANS_TYPE_CARDS" }),
    "data.showCardCount",
    0
  );
  productVariants
    .slice(0, numProductsShown || undefined)
    .forEach((productVariant, index) => {
      items.push({
        item_name: get(productVariant, "title", null),
        item_id: get(productVariant, "sku", null),
        price: get(productVariant, "price", null),
        item_brand: BRAND_NAME,
        item_category: get(ITEM_CATEGORY, "PLAN"), // All items in pre-checkout pages are plans
        index
      });
    });

  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "VIEW_ITEM_LIST", ""),
    ecommerce: {
      items,
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_platform: ORDER_PLATFORM,
      journey_type: JOURNEY_TYPE,
      order_channel: getOrderChannel(reduxStore)
    }
  };
};

export const constructLoadMoreViewItemListPayload = (data, reduxStore) => {
  const { showPlans } = data;
  let items = [];
  forEach(showPlans, (showPlan, index) => {
    items.push({
      item_name: get(showPlan, "title", null),
      item_id: get(showPlan, "sku", null),
      price: get(showPlan, "price", null),
      item_brand: BRAND_NAME,
      item_category: get(ITEM_CATEGORY, "PLAN"), // All items in pre-checkout pages are plans
      index
    });
  });
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "VIEW_ITEM_LIST", ""),
    ecommerce: {
      items,
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_platform: ORDER_PLATFORM,
      order_channel: getOrderChannel(reduxStore),
      journey_type: JOURNEY_TYPE
    }
  };
};

export const constructSelectItemMarketingPayload = (
  data,
  reduxStore,
  userSelectedPlan = {},
  allUserSelectedItems = []
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "SELECT_ITEM", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      items: constructItems(allUserSelectedItems),
      order_platform: ORDER_PLATFORM,
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const constructSelectItemPayload = (
  data,
  reduxStore,
  userSelectedPlan = {}
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "SELECT_ITEM", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      items: [
        {
          item_name: get(userSelectedPlan, "title", null),
          item_id: get(userSelectedPlan, "sku", null),
          price: get(userSelectedPlan, "price", null),
          item_brand: BRAND_NAME,
          item_category: get(ITEM_CATEGORY, "PLAN"),
          index: 0,
          quantity: 1
        }
      ],
      order_platform: ORDER_PLATFORM,
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const constructOtpSubmittedPayload = (
  data,
  reduxStore,
  userSelectedPlan = {}
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "OTP_SUBMITTED", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      order_platform: ORDER_PLATFORM,
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      }),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const constructEmailSubmittedPayload = (
  data,
  reduxStore,
  userSelectedPlan = {}
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "EMAIL_SUBMITTED", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      order_platform: ORDER_PLATFORM,
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      }),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const constructOtpValidateSubmittedPayload = (
  data,
  reduxStore,
  userSelectedPlan = {}
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "OTP_SUBMITTED_EMAIL", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      order_platform: ORDER_PLATFORM,
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      }),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const constructEmailSubmitButtonEnablePayload = (
  data,
  reduxStore,
  userSelectedPlan = {}
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "EMAIL_INPUT_COMPLETE", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      order_platform: ORDER_PLATFORM,
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      }),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const constructEmailSubmitButtonClickPayload = (
  data,
  reduxStore,
  userSelectedPlan = {}
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "VERIFY_EMAIL_CLICK", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      order_platform: ORDER_PLATFORM,
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      }),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const constructEmailReSubmittedPayload = (
  data,
  reduxStore,
  userSelectedPlan = {}
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "RESEND_OTT_CODE", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      order_platform: ORDER_PLATFORM,
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      }),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const constructUserLoggedInPayload = (
  data,
  reduxStore,
  userSelectedPlan = {}
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "USER_LOGGED_IN", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      order_platform: ORDER_PLATFORM,
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      }),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const constructBeginCheckoutPayload = (
  data,
  reduxStore,
  userSelectedPlan = {},
  allUserSelectedItems = []
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "BEGIN_CHECKOUT", ""),
    ecommerce: {
      currency: getStateData(
        reduxStore,
        "api.appGlobalSettings.data.currency",
        null
      ),
      value: allUserSelectedItems.reduce(
        (total, selectedAddon) => total + get(selectedAddon, "price", 0),
        0
      ),
      sim_type: get(
        SIM_TYPE_MAPPING,
        `${get(reduxStore, "ConnectionDetailsForm.values.sim_type")}`,
        SIM_TYPE_MAPPING.PHYSICAL_SIM
      ),
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      sim_count: 1,
      items: constructItems(allUserSelectedItems),
      order_platform: ORDER_PLATFORM,
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null),
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      })
    }
  };
};

export const constructSimTypeSelectedPayload = (
  data,
  reduxStore,
  userSelectedPlan = {}
) => {
  localStorage.setItem(
    "simtype",
    get(SIM_TYPE_MAPPING, `${get(data, "formValues.sim_type")}`, null)
  );
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "SIM_TYPE_SELECTED", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      sim_type: get(
        SIM_TYPE_MAPPING,
        `${get(data, "formValues.sim_type")}`,
        null
      ),
      order_platform: ORDER_PLATFORM,
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null),
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      })
    }
  };
};

export const constructNumberTypeSelectedPayload = (
  data,
  reduxStore,
  userSelectedPlan = {}
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "NUMBER_TYPE_SELECTED", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      number_type: get(
        NUMBER_TYPE_MAPPING,
        `${get(data, "formValues.number_type")}`,
        null
      ),
      order_platform: ORDER_PLATFORM,
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null),
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      })
    }
  };
};

export const constructRetailsIconClickPayload = (
  data,
  reduxStore,
  userSelectedPlan = {}
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "RETAIL_ICON_CLICK", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      sim_type: localStorage.getItem("simtype") ?? null,
      order_channel: get(ORDER_CHANNELS, "RETAIL", null),
      order_platform: ORDER_PLATFORM,
      storeId: get(data, "storeId"),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const constructStoreIdEnteredPayload = (
  data,
  reduxStore,
  userSelectedPlan = {}
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "STORE_ID_ENTERED", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      sim_type: localStorage.getItem("simtype") ?? null,
      order_channel: get(ORDER_CHANNELS, "RETAIL", null),
      order_platform: ORDER_PLATFORM,
      storeId: getStateData(
        reduxStore,
        "form.PersonalDetailsForm.values.storeId"
      ),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const constructSelfPickupStationsRequestedPayload = (
  data,
  reduxStore,
  userSelectedPlan = {}
) => {
  let isStoreIdEmpty = isEmpty(
    getStateData(reduxStore, "form.PersonalDetailsForm.values.storeId")
  );
  return {
    event: isStoreIdEmpty
      ? get(GTM_ECOMMERCE_EVENT_NAMES, "SELF_PICKUP_STATION_REQUESTED", "")
      : "store_id_popup_proceed_click",
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      sim_type: localStorage.getItem("simtype") ?? null,
      order_channel: get(ORDER_CHANNELS, "RETAIL", null), //getOrderChannel(reduxStore),
      order_platform: ORDER_PLATFORM,
      storeId: getStateData(
        reduxStore,
        "form.PersonalDetailsForm.values.storeId"
      ),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const constructSelfPickupStationsSucceededPayload = (
  data,
  reduxStore,
  userSelectedPlan = {}
) => {
  let isStoreIdValid =
    getStateData(reduxStore, "api.storeIdValidationInfo.data.valid") === true
      ? true
      : false;
  if (isStoreIdValid) {
    localStorage.setItem("isRetail", "1");
    localStorage.setItem(
      "storeId",
      getStateData(reduxStore, "form.PersonalDetailsForm.values.storeId")
    );
  }
  return {
    event: isStoreIdValid
      ? "store_id_validation_success"
      : "store_id_validation_failed",
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      sim_type: localStorage.getItem("simtype") ?? null,
      order_channel: get(ORDER_CHANNELS, "RETAIL", null),
      order_platform: ORDER_PLATFORM,
      storeId: getStateData(
        reduxStore,
        "form.PersonalDetailsForm.values.storeId"
      ),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const constructStoreIdDeleteClickPayload = (
  data,
  reduxStore,
  userSelectedPlan = {}
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "STORE_ID_DELETE_CLICK", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      sim_type: localStorage.getItem("simtype") ?? null,
      order_channel: get(ORDER_CHANNELS, "RETAIL", null),
      order_platform: ORDER_PLATFORM,
      storeId: getStateData(
        reduxStore,
        "form.PersonalDetailsForm.values.storeId"
      ),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const constructStoreIdPopupCancelClickPayload = (
  data,
  reduxStore,
  userSelectedPlan = {}
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "STORE_ID_POPUP_CANCEL_CLICK", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      sim_type: localStorage.getItem("simtype") ?? null,
      order_channel: get(ORDER_CHANNELS, "RETAIL", null), //getOrderChannel(reduxStore),
      order_platform: ORDER_PLATFORM,
      storeId: getStateData(
        reduxStore,
        "form.PersonalDetailsForm.values.storeId"
      ),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const constructOtpTimerExpirePayload = (
  data,
  reduxStore,
  userSelectedPlan = {}
) => {
  return {
    event: get(
      GTM_ECOMMERCE_EVENT_NAMES,
      "OTP_MODAL_EXPIRE_MODAL_REQUESTED",
      ""
    ),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      order_platform: ORDER_PLATFORM,
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      }),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const constructAddShippingInfoPayload = (
  data,
  reduxStore,
  userSelectedPlan = {},
  allUserSelectedItems = []
) => {
  let selected_number =
    getStateData(reduxStore, "api.lockedNumber.data.number") ||
    getStateData(reduxStore, "api.numbers.data.consumedNumbers[0]") ||
    getStateData(reduxStore, "api.numbers.data.selectedNumber.number") ||
    null;
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "ADD_SHIPPING_INFO", ""),
    ecommerce: {
      currency: get(data, "currency", null),
      value: allUserSelectedItems.reduce(
        (total, selectedAddon) => total + get(selectedAddon, "price", 0),
        0
      ),
      sim_type: get(
        SIM_TYPE_MAPPING,
        `${get(data, "forms.ConnectionDetailsForm.values.sim_type")}`,
        null
      ),
      number_type: get(
        NUMBER_TYPE_MAPPING,
        `${get(data, "forms.ConnectionDetailsForm.values.number_type")}`,
        null
      ),
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      portin_donor:
        get(data, "forms.ConnectionDetailsForm.values.number_type", null) ===
        get(CONTRACT_TYPES, "PORT_IN")
          ? true
          : false,
      sim_count: 1,
      selected_number,
      items: constructItems(allUserSelectedItems),
      order_platform: ORDER_PLATFORM,
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null),
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      })
    }
  };
};

export const constructPlaceOrderPayload = (
  data,
  reduxStore,
  userSelectedPlan = {},
  allUserSelectedItems = []
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "PLACE_ORDER", ""),
    ecommerce: {
      transaction_id: get(data, "order.orderReference", null),
      tax: get(data, "tax", 0),
      shipping: get(data, "initialShipmentDetails.price.price", 0),
      coupon: get(data, "promoCodes", null) || null,
      currency: get(data, "currency", null),
      value: get(data, "cost.amount", null),
      sim_type: localStorage.getItem("simtype") ?? null,
      number_type: get(
        NUMBER_TYPE_MAPPING,
        `${get(data, "forms.ConnectionDetailsForm.values.number_type")}`,
        null
      ),
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      portin_donor: get(data, "userOrder.port_in_request", null),
      payment_status: get(data, "userOrder.payment_status", null),
      sim_count: 1,
      selected_number: get(data, "userOrder.selected_number", null),
      order_platform: ORDER_PLATFORM,
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      }),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null),
      payment_type: localStorage.getItem("GA4Event_payment_type") ?? "PayFast"
    }
  };
};

export const constructZeroAmtPlaceOrderPayload = (
  data,
  reduxStore,
  userSelectedPlan = {},
  allUserSelectedItems = []
) => {
  const portInDonor = get(data, "order.numberType", null) === "PORTIN_NUMBER";

  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "PLACE_ORDER", ""),
    ecommerce: {
      transaction_id: get(data, "order.orderReference", null),
      tax: get(data, "tax", 0),
      shipping: get(data, "initialShipmentDetails.price.price", 0),
      coupon: get(data, "promoCodes", null) || null,
      currency: get(data, "currency", null),
      value: get(data, "cost.amount", 0),
      sim_type: localStorage.getItem("simtype") ?? null,
      number_type: get(
        NUMBER_TYPE_MAPPING,
        `${get(data, "forms.ConnectionDetailsForm.values.number_type")}`,
        null
      ),
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      portin_donor: portInDonor,
      payment_status: get(data, "order.payment_status", "N/A"),
      sim_count: 1,
      selected_number: get(data, "order.number", null),
      order_platform: ORDER_PLATFORM,
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      }),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const constructPurchasePayload = (
  data,
  reduxStore,
  userSelectedPlan = {},
  allUserSelectedItems = []
) => {
  const order = get(data, "order", {});
  const { billingDetails, initialShipmentDetails } = order;
  let customerBillingStreet = "";
  let customerBillingCity = "";
  let customerBillingRegion = "";
  let customerPostalCode = "";
  const {
    addressLine1: billingAddressLine1,
    addressLine2: billingAddressLine2,
    addressLine3: billingAddressLine3,
    city: billingCity,
    area: billingRegion,
    postalCode: billingPostalCode
  } = billingDetails;
  const {
    addressLine1: shippingAddressLine1,
    addressLine2: shippingAddressLine2,
    addressLine3: shippingAddressLine3,
    city: shippingCity,
    area: shippingRegion,
    postalCode: shippingPostalCode
  } = initialShipmentDetails;

  if (billingAddressLine1) customerBillingStreet += billingAddressLine1;
  if (billingAddressLine2) customerBillingStreet += ", " + billingAddressLine2;
  if (billingAddressLine3) customerBillingStreet += ", " + billingAddressLine3;
  if (!customerBillingStreet) {
    if (shippingAddressLine1) customerBillingStreet += shippingAddressLine1;
    if (shippingAddressLine2)
      customerBillingStreet += ", " + shippingAddressLine2;
    if (shippingAddressLine3)
      customerBillingStreet += ", " + shippingAddressLine3;
  }
  customerBillingCity = billingCity
    ? billingCity
    : shippingCity
    ? shippingCity
    : null;
  customerBillingRegion = billingRegion
    ? billingRegion
    : shippingRegion
    ? shippingRegion
    : null;
  customerPostalCode = billingPostalCode
    ? billingPostalCode
    : shippingPostalCode
    ? shippingPostalCode
    : null;

  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "PURCHASE", ""),
    ecommerce: {
      transaction_id: get(data, "order.orderReference", null),
      tax: get(data, "order.tax", 0),
      shipping: get(data, "order.initialShipmentDetails.price.price", null),
      coupon: get(data, "order.promoCodes", null) || null,
      currency: get(data, "currency", null),
      value: get(data, "charge_amount", null),
      sim_type: get(SIM_TYPE_MAPPING, `${get(data, "order.simType")}`, null),
      number_type: get(
        NUMBER_TYPE_MAPPING,
        `${get(data, "order.numberType")}`,
        null
      ),
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: get(data, "user_id", null),
      order_channel: isRetail() ? "retail" : "digital", //getOrderChannel(reduxStore),
      portin_donor: get(data, "userOrder.port_in_request", null),
      payment_status: get(data, "payment_status", null),
      sim_count: 1,
      selected_number: get(data, "order.number", null),
      items: constructItems(allUserSelectedItems),
      order_platform: ORDER_PLATFORM,
      ...(isRetail() && {
        storeId: getItem("storeId")
      }),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null),
      payment_type: localStorage.getItem("GA4Event_payment_type") ?? "PayFast",
      enhanced_conversion_data: {
        // All data here should be SHA-256 hashed
        customerEmail: get(data, "order.userDetails.email")
          ? Sha256(get(data, "order.userDetails.email")).toString()
          : null,
        customerPhone: get(data, "order.userDetails.contactNumber")
          ? Sha256(get(data, "order.userDetails.contactNumber")).toString()
          : null,
        customerFirstName: get(data, "order.userDetails.firstName")
          ? Sha256(get(data, "order.userDetails.firstName")).toString()
          : null,
        customerLastName: get(data, "order.userDetails.lastName")
          ? Sha256(get(data, "order.userDetails.lastName")).toString()
          : null,
        customerStreet: customerBillingStreet
          ? Sha256(customerBillingStreet).toString()
          : null,
        customerCity: customerBillingCity
          ? Sha256(customerBillingCity).toString()
          : null,
        customerRegion: customerBillingRegion
          ? Sha256(customerBillingRegion).toString()
          : null,
        customerCountry: get(data, "order.billingDetails.country")
          ? Sha256(get(data, "order.billingDetails.country")).toString()
          : null,
        customerPostalCode: customerPostalCode
          ? Sha256(customerPostalCode).toString()
          : null
      }
    }
  };
};

export const constructZeroAmtPurchasePayload = (
  data,
  reduxStore,
  userSelectedPlan = {},
  allUserSelectedItems = []
) => {
  const order = get(data, "order", {});
  const { billingDetails, initialShipmentDetails } = order;
  let customerBillingStreet = "";
  let customerBillingCity = "";
  let customerBillingRegion = "";
  let customerPostalCode = "";
  const {
    addressLine1: billingAddressLine1,
    addressLine2: billingAddressLine2,
    addressLine3: billingAddressLine3,
    city: billingCity,
    area: billingRegion,
    postalCode: billingPostalCode
  } = billingDetails;
  const {
    addressLine1: shippingAddressLine1,
    addressLine2: shippingAddressLine2,
    addressLine3: shippingAddressLine3,
    city: shippingCity,
    area: shippingRegion,
    postalCode: shippingPostalCode
  } = initialShipmentDetails;

  if (billingAddressLine1) customerBillingStreet += billingAddressLine1;
  if (billingAddressLine2) customerBillingStreet += ", " + billingAddressLine2;
  if (billingAddressLine3) customerBillingStreet += ", " + billingAddressLine3;
  if (!customerBillingStreet) {
    if (shippingAddressLine1) customerBillingStreet += shippingAddressLine1;
    if (shippingAddressLine2)
      customerBillingStreet += ", " + shippingAddressLine2;
    if (shippingAddressLine3)
      customerBillingStreet += ", " + shippingAddressLine3;
  }
  customerBillingCity = billingCity
    ? billingCity
    : shippingCity
    ? shippingCity
    : null;
  customerBillingRegion = billingRegion
    ? billingRegion
    : shippingRegion
    ? shippingRegion
    : null;
  customerPostalCode = billingPostalCode
    ? billingPostalCode
    : shippingPostalCode
    ? shippingPostalCode
    : null;
  const portInDonor = get(data, "order.numberType", null) === "PORTIN_NUMBER";

  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "PURCHASE", ""),
    ecommerce: {
      transaction_id: get(data, "order.orderReference", null),
      tax: get(data, "order.tax", 0),
      shipping: get(data, "order.initialShipmentDetails.price.price", null),
      coupon: get(data, "order.promoCodes", null) || null,
      currency: get(data, "currency", null),
      value: get(data, "charge_amount", 0),
      sim_type: get(SIM_TYPE_MAPPING, `${get(data, "order.simType")}`, null),
      number_type: get(
        NUMBER_TYPE_MAPPING,
        `${get(data, "order.numberType")}`,
        null
      ),
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: isRetail() ? "retail" : "digital", //getOrderChannel(reduxStore),
      portin_donor: portInDonor,
      payment_status: get(data, "payment_status", "N/A"),
      sim_count: 1,
      selected_number: get(data, "order.number", null),
      items: constructItems(allUserSelectedItems),
      order_platform: ORDER_PLATFORM,
      ...(isRetail() && {
        storeId: getItem("storeId")
      }),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null),
      enhanced_conversion_data: {
        // All data here should be SHA-256 hashed
        customerEmail: get(data, "order.userDetails.email")
          ? Sha256(get(data, "order.userDetails.email")).toString()
          : null,
        customerPhone: get(data, "order.userDetails.contactNumber")
          ? Sha256(get(data, "order.userDetails.contactNumber")).toString()
          : null,
        customerFirstName: get(data, "order.userDetails.firstName")
          ? Sha256(get(data, "order.userDetails.firstName")).toString()
          : null,
        customerLastName: get(data, "order.userDetails.lastName")
          ? Sha256(get(data, "order.userDetails.lastName")).toString()
          : null,
        customerStreet: customerBillingStreet
          ? Sha256(customerBillingStreet).toString()
          : null,
        customerCity: customerBillingCity
          ? Sha256(customerBillingCity).toString()
          : null,
        customerRegion: customerBillingRegion
          ? Sha256(customerBillingRegion).toString()
          : null,
        customerCountry: get(data, "order.billingDetails.country")
          ? Sha256(get(data, "order.billingDetails.country")).toString()
          : null,
        customerPostalCode: customerPostalCode
          ? Sha256(customerPostalCode).toString()
          : null
      }
    }
  };
};

export const constructPersonalDetailFormStartPayload = (
  data,
  reduxStore,
  userSelectedPlan = {}
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "PERSONAL_DETAIL_FORM_START", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      order_platform: ORDER_PLATFORM,
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      }),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const userPersonalInformationFilledAndValid = (
  formValues = {},
  formSyncErrors = {},
  reduxStore,
  errors = false
) => {
  const personal_details = getStateData(
    reduxStore,
    "api.newProfileParameters.data",
    []
  );
  const isCNICExpiryDateEnabled = isFormFieldEnabled(
    personal_details,
    "email",
    "CNICExpiryDate"
  );

  const userPersonalInfo = {
    first_name: get(formValues, "first_name", ""),
    last_name: get(formValues, "last_name", ""),
    dob: get(formValues, "dob", ""),
    contact_number: get(formValues, "contact_number", ""),
    nationality: get(formValues, "nationality.value", ""),
    identification_number: get(formValues, "identification_number", ""),
    ...(!isRetail() && {
      reconfirm_identification_number: get(
        formValues,
        "reConfirmidentificationNumber",
        ""
      )
    })
  };

  const userPersonalInfoFormErrors = {
    first_name: get(formSyncErrors, "first_name", ""),
    last_name: get(formSyncErrors, "last_name", ""),
    dob: get(formSyncErrors, "dob", ""),
    contact_number: get(formSyncErrors, "contact_number", ""),
    nationality: get(formSyncErrors, "nationality", ""),
    identification_number: get(formSyncErrors, "identification_number", ""),
    ...(!isRetail() && {
      reconfirm_identification_number: get(
        formSyncErrors,
        "reConfirmidentificationNumber",
        ""
      )
    })
  };

  const userPersonalInfoFormwithAddressErrors = {
    ...userPersonalInfoFormErrors,
    email: get(formSyncErrors, "email", ""),
    address1: get(formSyncErrors, "delivery_address_line_1", ""),
    city: get(formSyncErrors, "delivery_city", ""),
    province: get(formSyncErrors, "delivery_province", ""),
    area: get(formSyncErrors, "delivery_area", "")
  };

  if (isCNICExpiryDateEnabled) {
    userPersonalInfo.document_identificationNumberExpiryDate = get(
      formValues,
      "document_identificationNumberExpiryDate",
      ""
    );
    userPersonalInfoFormErrors.document_identificationNumberExpiryDate = get(
      formSyncErrors,
      "document_identificationNumberExpiryDate",
      ""
    );
    userPersonalInfoFormwithAddressErrors.document_identificationNumberExpiryDate = get(
      formSyncErrors,
      "document_identificationNumberExpiryDate",
      ""
    );
  }
  const allFieldsFilled = Object.values(userPersonalInfo).every(value => value);
  const allFieldsValid = Object.values(userPersonalInfoFormErrors).every(
    value => !value
  );

  if (errors) {
    return userPersonalInfoFormwithAddressErrors;
  } else {
    return allFieldsFilled && allFieldsValid;
  }
};

export const constructPersonalDetailFormCompletePayload = (
  data,
  reduxStore,
  userSelectedPlan = {}
) => {
  // const isStoreIdValid = getStateData(
  //   reduxStore,
  //   "api.storeIdValidationInfo.data.valid",
  //   []
  // );
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "PERSONAL_DETAIL_FORM_COMPLETE", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      order_platform: ORDER_PLATFORM,
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      }),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const constructDeliveryDetailFormStartPayload = (
  data,
  reduxStore,
  userSelectedPlan = {}
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "DELIVERY_DETAIL_FORM_START", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      order_platform: ORDER_PLATFORM,
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null),
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      })
    }
  };
};

export const userDeliveryInformationFilledAndValid = (
  formValues = {},
  formSyncErrors = {},
  userSelectedPlan = {}
) => {
  const plan_type = get(userSelectedPlan, "productCategory", "");
  let billingType =
    plan_type === get(PLAN_TYPE, "POSTPAID", "")
      ? "billing"
      : plan_type === get(PLAN_TYPE, "PREPAID", "")
      ? "delivery"
      : "";
  const userDeliveryInfo = {
    address_line_1: get(formValues, `${billingType}_address_line_1`, ""),
    province: get(formValues, `${billingType}_province.value`, ""),
    city: get(formValues, `${billingType}_city.value`, ""),
    area: get(formValues, `${billingType}_area.value`, "")
  };
  const userDeliveryInfoFormErrors = {
    address_line_1: get(formSyncErrors, `${billingType}_address_line_1`, ""),
    province: get(formSyncErrors, `${billingType}_province`, ""),
    city: get(formSyncErrors, `${billingType}_city`, ""),
    area: get(formSyncErrors, `${billingType}_area`, "")
  };
  const allFieldsFilled = Object.values(userDeliveryInfo).every(value => value);
  const allFieldsValid = Object.values(userDeliveryInfoFormErrors).every(
    value => !value
  );

  return allFieldsFilled && allFieldsValid;
};

export const constructDeliveryDetailFormCompletePayload = (
  data,
  reduxStore,
  userSelectedPlan = {}
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "DELIVERY_DETAIL_FORM_COMPLETE", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      order_platform: ORDER_PLATFORM,
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null),
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      })
    }
  };
};

export const constructCNICSubmittedPayload = (
  data,
  reduxStore,
  userSelectedPlan
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "CNIC_SUBMITTED", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      order_platform: ORDER_PLATFORM,
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      }),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const constructContactNumberSubmittedPayload = (
  data,
  reduxStore,
  userSelectedPlan
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "CONTACT_NO_SUBMITTED", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      order_platform: ORDER_PLATFORM,
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      }),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const constructNationalitySubmittedPayload = (
  data,
  reduxStore,
  userSelectedPlan
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "NATIONALITY_SUBMITTED", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      order_platform: ORDER_PLATFORM,
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      }),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null)
    }
  };
};

export const constructPaymentPendingPayload = (
  data,
  reduxStore,
  userSelectedPlan
) => {
  const { data: orderData } = getStateData(reduxStore, "api.order", {});
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "PAYMENT_PENDING", ""),
    ecommerce: {
      transaction_id: get(orderData, "orderReference", null),
      tax: get(orderData, "tax", 0),
      shipping: get(orderData, "initialShipmentDetails.price.price", null),
      coupon: get(orderData, "promoCodes", null),
      currency: get(orderData, "cost.currency"),
      value: get(orderData, "cost.amount"),
      sim_type: get(SIM_TYPE_MAPPING, `${get(orderData, "simType")}`, null),
      number_type: get(
        NUMBER_TYPE_MAPPING,
        `${get(orderData, "numberType", "")}`,
        null
      ),
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      portin_donor:
        get(orderData, "numberType", null) === get(CONTRACT_TYPES, "PORT_IN")
          ? true
          : false,
      payment_status: getStateData(
        reduxStore,
        "api.paymentStatus.data.payment_status",
        "Pending"
      ),
      sim_count: 1,
      selected_number: get(orderData, "number"),
      order_platform: ORDER_PLATFORM,
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      }),
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null),
      payment_type: localStorage.getItem("GA4Event_payment_type") ?? "PayFast"
    }
  };
};

export const constructAddonSelectedPayload = (
  data,
  reduxStore,
  userSelectedPlan
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "ADD_ON_SELECTED", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      add_on_name: get(data, "addon.title", null),
      add_on_id: get(data, "addon.sku", null),
      order_platform: ORDER_PLATFORM,
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null),
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      })
    }
  };
};

export const constructAddonDeselectedPayload = (
  data,
  reduxStore,
  userSelectedPlan
) => {
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "ADD_ON_DESELECTED", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      add_on_name: get(data, "addon.title", null),
      add_on_id: get(data, "addon.sku", null),
      order_platform: ORDER_PLATFORM,
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null),
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      })
    }
  };
};

export const constructNumberSearchPayload = (data, reduxStore) => {
  const ConnectionDetailsForm = data.forms.ConnectionDetailsForm.values;

  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "NEW_NUMBER_SEARCH_RESULT", ""),
    ecommerce: {
      sim_type: get(
        SIM_TYPE_MAPPING,
        `${get(ConnectionDetailsForm, "sim_type")}`,
        null
      ),
      number_type: get(ConnectionDetailsForm, "number_type", null),
      search_criteria: get(ConnectionDetailsForm, "number_category", null), // need confirmation on search_criteria
      count: get(data, "availableNumbers.length", 0),
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_platform: ORDER_PLATFORM
    }
  };
};

export const constructNumberSelectedPayload = (data, reduxStore) => {
  const ConnectionDetailsForm = data.forms.ConnectionDetailsForm.values;
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "NUMBER_SELECTED", ""),
    ecommerce: {
      sim_type: get(
        SIM_TYPE_MAPPING,
        `${get(ConnectionDetailsForm, "sim_type")}`,
        null
      ),
      number_type: get(ConnectionDetailsForm, "number_type", null), //need confirmation on number_tier
      number_tier: get(ConnectionDetailsForm, "number_category", null),
      esim_consent: get(
        ConnectionDetailsForm,
        "e_sim_consent_disclaimer",
        false
      ),
      number: get(data, "number", null),
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_platform: ORDER_PLATFORM
    }
  };
};

export const constructPortInNumberPayload = (
  data,
  reduxStore,
  userSelectedPlan
) => {
  let validation_errors = {
    port_number: data.formSyncErrors
      ? get(data, "formSyncErrors.port_number", "")
      : get(data, "formErrors.port_number", ""),
    telco: data.formSyncErrors
      ? get(data, "formSyncErrors.telco", "")
      : get(data, "formErrors.telco", "")
  };

  validation_errors = filterNullValues(validation_errors);
  let status = Object.keys(validation_errors).length >= 1 ? "Fail" : "Success";
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "PORT_IN_NUMBER_SELECTED", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      number_type: data.formValues
        ? get(
            NUMBER_TYPE_MAPPING,
            `${get(data, "formsValues.number_type")}`,
            null
          )
        : get(
            NUMBER_TYPE_MAPPING,
            `${get(data, "forms.ConnectionDetailsForm.values.number_type")}`,
            null
          ),
      sim_type: data.formValues
        ? get(SIM_TYPE_MAPPING, `${get(data, "formValues.sim_type")}`, null)
        : get(
            SIM_TYPE_MAPPING,
            `${get(data, "forms.ConnectionDetailsForm.values.sim_type")}`,
            null
          ),
      current_number: data.formValues
        ? get(data, "formValues.port_number", null)
        : get(data, "forms.ConnectionDetailsForm.values.port_number", null),
      current_telco: data.formValues
        ? get(data, "formValues.telco.value", null)
        : get(data, "forms.ConnectionDetailsForm.values.telco.value", null),
      validation_errors: validation_errors,
      status: status,
      order_platform: ORDER_PLATFORM
    }
  };
};

export const constructPersonalDetailsErrorPayload = (
  data,
  reduxStore,
  userSelectedPlan
) => {
  const { formValues, formErrors } = data;
  let validation_errors = userPersonalInformationFilledAndValid(
    formValues,
    formErrors,
    reduxStore,
    true
  );

  validation_errors = filterNullValues(validation_errors);
  let status = Object.keys(validation_errors).length >= 1 ? "Fail" : "Success";

  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "USER_DETAILS_ENTERED", ""),

    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      validation_errors: validation_errors,
      status: status,
      order_platform: ORDER_PLATFORM
    }
  };
};

export const constructKycInfoDetailsErrorPayload = (
  data,
  reduxStore,
  userSelectedPlan
) => {
  const validation_errors = {
    identification_number: CNIC_ERROR,
    reConfirmidentificationNumber: get(
      data,
      "formErrors.reConfirmidentificationNumber",
      null
    ),
    document_identificationNumberExpiryDate: get(
      data,
      "formErrors.document_identificationNumberExpiryDate",
      null
    )
  };

  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "ADD_KYC_INFO", ""),

    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      validation_errors: validation_errors,
      order_platform: ORDER_PLATFORM
    }
  };
};

export const constructGA4EventData = (
  data,
  reduxStore,
  userSelectedPlan = {},
  eventName
) => {
  return {
    event: eventName,
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      plan_name: get(userSelectedPlan, "title", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      order_channel: getOrderChannel(reduxStore),
      journey_type: JOURNEY_TYPE,
      sim_type: localStorage.getItem("simtype") ?? null,
      order_platform: ORDER_PLATFORM,
      storeId: getStateData(
        reduxStore,
        "form.PersonalDetailsForm.values.storeId"
      ),
      payment_type: localStorage.getItem("GA4Event_payment_type") ?? "PayFast"
    }
  };
};

export const constructAccessLocationEventData = (
  data,
  reduxStore,
  userSelectedPlan = {},
  eventName
) => {
  return {
    event: eventName,
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", "prepaid"),
      order_flow: "NON-SAD",
      order_channel: getOrderChannel(reduxStore),
      journey_type: JOURNEY_TYPE,
      order_platform: ORDER_PLATFORM
    }
  };
};

export const constructAccessLocationConsentEventData = (
  data,
  reduxStore,
  userSelectedPlan = {},
  eventName
) => {
  const locationData = getStateData(
    reduxStore,
    "api.locationconsentgiven.data",
    null
  );
  return {
    event: eventName,
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", "prepaid"),
      order_flow: "NON-SAD",
      order_channel: getOrderChannel(reduxStore),
      journey_type: JOURNEY_TYPE,
      order_platform: ORDER_PLATFORM,
      location: locationData?.location,
      latitude: locationData?.latitude,
      longitude: locationData?.longitude
    }
  };
};

function filterNullValues(inputObject) {
  return Object.fromEntries(
    Object.entries(inputObject).filter(
      ([key, value]) => value !== "" && value !== undefined
    )
  );
}

function isFormFieldEnabled(groups, groupName, attributeName) {
  let isFieldEnabled = false;
  const result = groups.find(group => group.groupName === groupName);
  if (result) {
    const attribute = result.attributes.find(
      attribute => attribute.name === attributeName
    );
    isFieldEnabled = attribute.enabled;
  }
  return isFieldEnabled;
}

export const constructPromoCodeApplied = (
  data,
  reduxStore,
  userSelectedPlan
) => {
  const ConnectionDetailsForm = data.forms.ConnectionDetailsForm.values;
  let promoCodeValues = getPromoCode();

  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "PROMO_CODE_APPLIED", ""),
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      sim_type: get(
        SIM_TYPE_MAPPING,
        `${get(ConnectionDetailsForm, "sim_type")}`,
        null
      ),
      add_on_name: get(data, "addon.title", null),
      add_on_id: get(data, "addon.sku", null),
      order_channel: getOrderChannel(reduxStore),
      promo_code: promoCodeValues,
      order_platform: ORDER_PLATFORM,
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null),
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      })
    }
  };
};

export const constructNavigationType = (
  data,
  reduxStore,
  userSelectedPlan,
  navigationType
) => {
  return {
    event: `${navigationType}_click`,
    ecommerce: {
      plan_type: get(userSelectedPlan, "productCategory", null),
      plan_id: get(userSelectedPlan, "sku", null),
      order_flow: "NON-SAD",
      customer_id: getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.external_id",
        null
      ),
      sim_type: localStorage.getItem("simtype") ?? null,
      add_on_name: get(data, "addon.title", null),
      add_on_id: get(data, "addon.sku", null),
      order_channel: getOrderChannel(reduxStore),
      order_platform: ORDER_PLATFORM,
      journey_type: JOURNEY_TYPE,
      plan_name: get(userSelectedPlan, "title", null),
      ...(isRetail() && {
        storeId: getStateData(
          reduxStore,
          "form.PersonalDetailsForm.values.storeId"
        )
      })
    }
  };
};

export const constructUserDetailsPayload = () => {
  const user_ipaddress = Sha256(getIpAddress()).toString();
  return {
    event: get(GTM_ECOMMERCE_EVENT_NAMES, "USER_DETAILS", ""),
    order_platform: ORDER_PLATFORM,
    user_id: user_ipaddress,
    journey_type: JOURNEY_TYPE
  };
};
